import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const NAV_ITEMS = [
  { label: '首页', to: '/' },
  { label: '电影', to: '/category/movie' },
  { label: '电视剧', to: '/category/tv' },
  { label: '资讯', to: '/news' },
  { label: '影评', to: '/special' },
  { label: '关于', to: '/about' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setKeyword('');
  };

  const isActive = (to: string) => {
    if (to === '/') return location.pathname === '/';
    return location.pathname.startsWith(to);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-colors duration-300 ${
        scrolled || menuOpen
          ? 'bg-background-100/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="h-16 md:h-[72px] flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500 text-accent-400">
              <i className="ri-film-line text-xl"></i>
            </span>
            <span className="font-heading text-xl font-bold tracking-wide text-foreground-950">
              御宅屋
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`px-3.5 py-2 text-sm rounded-md whitespace-nowrap transition-colors ${
                  isActive(item.to)
                    ? 'text-accent-400 font-medium'
                    : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-200/60'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <form
              onSubmit={handleSearch}
              className="hidden md:flex items-center gap-2 bg-background-200/70 border border-background-300/60 rounded-full pl-3 pr-1 py-1"
            >
              <i className="ri-search-line text-foreground-500 text-sm"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                type="text"
                name="q"
                placeholder="搜索影视 / 资讯"
                className="bg-transparent outline-none text-sm text-foreground-900 placeholder:text-foreground-500 w-32 lg:w-44"
              />
              <button
                type="submit"
                className="shrink-0 px-3 py-1.5 text-xs rounded-full bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                搜索
              </button>
            </form>

            <Link
              to="/special"
              className="hidden xl:inline-flex items-center gap-1.5 px-3.5 py-2 text-sm rounded-md bg-primary-500/90 text-foreground-950 hover:bg-primary-500 transition-colors whitespace-nowrap"
            >
              <i className="ri-sparkling-2-line"></i>
              专题
            </Link>

            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="打开菜单"
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-md text-foreground-900 hover:bg-background-200/70 transition-colors cursor-pointer"
            >
              <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden border-t border-background-200/70 bg-background-100/98 backdrop-blur-md">
          <div className="px-4 py-4 flex flex-col gap-1">
            <form
              onSubmit={handleSearch}
              className="flex items-center gap-2 bg-background-200/70 border border-background-300/60 rounded-full pl-3 pr-1 py-1 mb-2"
            >
              <i className="ri-search-line text-foreground-500 text-sm"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                type="text"
                name="q"
                placeholder="搜索影视 / 资讯"
                className="bg-transparent outline-none text-sm text-foreground-900 placeholder:text-foreground-500 flex-1"
              />
              <button
                type="submit"
                className="shrink-0 px-3 py-1.5 text-xs rounded-full bg-accent-500 text-background-50 cursor-pointer whitespace-nowrap"
              >
                搜索
              </button>
            </form>
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`px-3 py-2.5 text-sm rounded-md transition-colors ${
                  isActive(item.to)
                    ? 'text-accent-400 font-medium bg-background-200/60'
                    : 'text-foreground-700 hover:bg-background-200/60'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}